import { Dialog, PlainButton, TextInput } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import type {
  CustomTemplateData,
  CustomTemplateVariable,
} from "@jugl-web/rest-api";
import React, { useState } from "react";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

const EMPTY_TEMPLATE: CustomTemplateData = {
  name: "",
  description: "",
  method: "POST",
  url: "",
  headers: {},
  body: {},
  variables: [],
};

export interface CustomToolDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CustomTemplateData) => void;
  initial?: CustomTemplateData;
}

export const CustomToolDialog: React.FC<CustomToolDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initial,
}) => {
  const { t } = useTranslations();
  const [data, setData] = useState<CustomTemplateData>(
    initial ?? EMPTY_TEMPLATE
  );
  const [headersText, setHeadersText] = useState(
    JSON.stringify(initial?.headers ?? {}, null, 2)
  );
  const [bodyText, setBodyText] = useState(
    JSON.stringify(initial?.body ?? {}, null, 2)
  );

  const updateField = <K extends keyof CustomTemplateData>(
    key: K,
    value: CustomTemplateData[K]
  ) => setData((prev) => ({ ...prev, [key]: value }));

  const addVariable = () =>
    updateField("variables", [...data.variables, { name: "", desc: "" }]);

  const updateVariable = (
    idx: number,
    field: keyof CustomTemplateVariable,
    value: string
  ) =>
    updateField(
      "variables",
      data.variables.map((v, i) => (i === idx ? { ...v, [field]: value } : v))
    );

  const removeVariable = (idx: number) =>
    updateField(
      "variables",
      data.variables.filter((_, i) => i !== idx)
    );

  const handleSave = () => {
    let headers: Record<string, string> = {};
    let body: Record<string, unknown> = {};
    try {
      headers = JSON.parse(headersText);
    } catch {
      /* keep empty */
    }
    try {
      body = JSON.parse(bodyText);
    } catch {
      /* keep empty */
    }
    onSave({ ...data, headers, body });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-full max-w-[640px] flex-col"
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-6 px-6">
        <span className="font-secondary font-semibold text-[#383838]">
          {t({
            id: "cx-agent-setup.custom-tool",
            defaultMessage: "Custom Tool",
          })}
        </span>
        <PlainButton onClick={onClose}>
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 pb-4">
        <TextInput
          labelPosition="border-overlap"
          label={t({
            id: "cx-agent-setup.tool-name",
            defaultMessage: "Name",
          })}
          value={data.name ?? ""}
          onChange={(e) => updateField("name", e.target.value)}
        />
        <TextInput
          labelPosition="border-overlap"
          label={t({
            id: "cx-agent-setup.tool-description",
            defaultMessage: "Description",
          })}
          value={data.description ?? ""}
          onChange={(e) => updateField("description", e.target.value)}
        />
        <div className="flex gap-3">
          <div className="w-[120px] shrink-0">
            <TextInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-agent-setup.method",
                defaultMessage: "Method",
              })}
              value={data.method}
              onChange={(e) =>
                updateField("method", e.target.value.toUpperCase())
              }
            />
          </div>
          <div className="flex-1">
            <TextInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-agent-setup.url",
                defaultMessage: "URL",
              })}
              value={data.url}
              onChange={(e) => updateField("url", e.target.value)}
            />
          </div>
        </div>
        <TextAreaInput
          label={t({
            id: "cx-agent-setup.headers-json",
            defaultMessage: "Headers (JSON)",
          })}
          value={headersText}
          onChange={(e) => setHeadersText(e.target.value)}
          classNames={{ inputClassName: "min-h-[80px] font-mono text-xs" }}
        />
        <TextAreaInput
          label={t({
            id: "cx-agent-setup.body-json",
            defaultMessage: "Body (JSON)",
          })}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          classNames={{ inputClassName: "min-h-[100px] font-mono text-xs" }}
        />
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-secondary text-sm font-medium text-[#383838]">
              {t({
                id: "cx-agent-setup.variables",
                defaultMessage: "Variables",
              })}
            </span>
            <Button variant="secondary" size="sm" onClick={addVariable}>
              {t({
                id: "cx-agent-setup.add-variable",
                defaultMessage: "Add Variable",
              })}
            </Button>
          </div>
          {data.variables.map((v, idx) => (
            <div key={+idx} className="flex items-start gap-2">
              <TextInput
                classNames={{ wrapperClassName: "flex-1" }}
                placeholder={t({
                  id: "cx-agent-setup.variable-name",
                  defaultMessage: "Name",
                })}
                value={v.name}
                onChange={(e) => updateVariable(idx, "name", e.target.value)}
              />
              <TextInput
                classNames={{ wrapperClassName: "flex-1" }}
                placeholder={t({
                  id: "cx-agent-setup.variable-desc",
                  defaultMessage: "Description",
                })}
                value={v.desc}
                onChange={(e) => updateVariable(idx, "desc", e.target.value)}
              />
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => removeVariable(idx)}
                className="text-tertiary-800 mt-1 shrink-0"
              >
                {t({ id: "common.delete", defaultMessage: "Delete" })}
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3 px-6 py-4">
        <Button variant="secondary" onClick={onClose}>
          {t({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        <Button onClick={handleSave}>
          {t({ id: "common.save", defaultMessage: "Save" })}
        </Button>
      </div>
    </Dialog>
  );
};
